import React, { useState } from 'react';
import Loading from 'components/Loading';
import NoMatch from 'containers/NoMatch';
import useUser from 'hooks/useUser';
import Page from 'components/Page';
import { Badge, Button, Card, Col, Descriptions, List, Popconfirm, Row, Space, Typography } from 'antd';
import Table from 'components/Table';
import PhoneNumber from 'components/PhoneNumber';
import { useMutation } from 'hooks/apollo';
import { gql } from '@apollo/client';
import { Mfa } from 'shared/prisma/types';
import { MFA_METHOD_ORDER } from 'shared/constants/mfa';
import EditUserProfile from './EditUserProfile';
import EnableMFA from './EnableMFA';

const DISABLE_TOTP = gql`
  mutation($userId: ID!) {
    disableTotp(userId: $userId) {
      id
      mfa
      defaultMfa
      totpEnabled
      mfaMethods
    }
  }
`;

const UPDATE_USER = gql`
  mutation($input: UpdateUserInput!) {
    updateUser(input: $input) {
      id
      mfa
      defaultMfa
      totpEnabled
      mfaMethods
    }
  }
`;

const MFA_METHOD_LABELS = {
  [Mfa.Sms]: 'SMS',
  [Mfa.Email]: 'Email',
  [Mfa.Totp]: 'Authenticator app',
};

const ALL_MFA_METHODS = MFA_METHOD_ORDER;

function isMethodEnabled(user, method) {
  if (Array.isArray(user?.mfaMethods)) {
    return user.mfaMethods.includes(method);
  }

  if (method === Mfa.Email) {
    return !!user?.mfa;
  }

  if (method === Mfa.Totp) {
    return user.totpEnabled;
  }

  if (method === Mfa.Sms) {
    return !!user.phone;
  }

  return false;
}

function getEnabledMethods(user) {
  return ALL_MFA_METHODS.filter((method) => isMethodEnabled(user, method));
}

const UserProfile = () => {
  const { user, loading, refetch } = useUser();
  const [editUser, setEditUser] = useState(false);
  const [enableMfaMethod, setEnableMfaMethod] = useState();
  const [updateUser, { loading: updatingUser }] = useMutation(UPDATE_USER);
  const [disableTotp, { loading: disablingTotp }] = useMutation(DISABLE_TOTP);

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    return <NoMatch />;
  }

  const accountColumns = [
    {
      title: 'Account',
      dataIndex: ['customer', 'name'],
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      render: (roles) => roles?.map((role) => role.name).join(', '),
    },
  ];

  const dataSourceColumns = [
    {
      title: 'Data source',
      dataIndex: ['dataSource', 'title'],
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      render: (roles) => roles?.map((role) => role.name).join(', '),
    },
  ];

  const accountAccess = user?.customerAccess.filter((ca) => ca.roles.length > 0);
  const dataSourceAccess = accountAccess.flatMap((customerAccess) => customerAccess.dataSourceAccess.filter((wa) => wa.roles.length > 0));
  const enabledMethods = getEnabledMethods(user);
  const effectiveDefaultMfa = user.defaultMfa && enabledMethods.includes(user.defaultMfa)
    ? user.defaultMfa
    : enabledMethods[0];

  const onDefaultMfaChange = async (defaultMfa) => {
    await updateUser({
      variables: {
        input: {
          id: user.id,
          defaultMfa,
        },
      },
    });
    refetch();
  };

  const onDisableTotp = async () => {
    await disableTotp({ variables: { userId: user.id } });
    await refetch();
  };

  const onDisableMethod = async (method) => {
    if (method === Mfa.Totp) {
      await onDisableTotp();
      return;
    }

    const mfaMethods = enabledMethods.filter((enabledMethod) => enabledMethod !== method);
    const defaultMfa = mfaMethods.includes(user.defaultMfa) ? user.defaultMfa : mfaMethods[0] || null;

    await updateUser({
      variables: {
        input: {
          id: user.id,
          mfa: defaultMfa,
          defaultMfa,
          mfaMethods,
        },
      },
    });
    await refetch();
  };

  const getMethodDescription = (method) => {
    if (method === Mfa.Sms && !user.phone) {
      return 'Add a phone number to your profile to use SMS verification.';
    }

    if (method === Mfa.Email) {
      return user.email;
    }

    if (method === Mfa.Sms) {
      return <PhoneNumber number={user.phone} />;
    }

    return 'Use an authenticator app on your phone or computer.';
  };

  const getMethodActions = (method) => {
    const enabled = isMethodEnabled(user, method);
    const actionLoading = updatingUser || disablingTotp;

    return (
      <Space size="middle" wrap={false}>
        <Badge
          status={enabled ? 'success' : 'default'}
          text={enabled ? 'Enabled' : 'Disabled'}
        />
        {enabled && (
          method === effectiveDefaultMfa
            ? <Typography.Text type="secondary">Default</Typography.Text>
            : (
              <Button
                size="small"
                loading={actionLoading}
                onClick={() => onDefaultMfaChange(method)}
              >
                Set as default
              </Button>
            )
        )}
        {enabled && method === Mfa.Totp && (
          <Button
            size="small"
            disabled={actionLoading}
            onClick={() => setEnableMfaMethod(Mfa.Totp)}
          >
            Reconfigure
          </Button>
        )}
        {enabled && (
          <Popconfirm
            title={`Disable ${MFA_METHOD_LABELS[method]}?`}
            description={enabledMethods.length === 1
              ? 'You will be able to sign in with your password only.'
              : 'This method will no longer be available when you sign in.'}
            onConfirm={() => onDisableMethod(method)}
            okText="Disable"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger loading={actionLoading}>
              Disable
            </Button>
          </Popconfirm>
        )}
        {!enabled && method === Mfa.Sms && !user.phone && (
          <Button size="small" onClick={() => setEditUser(true)}>
            Add phone number
          </Button>
        )}
        {!enabled && !(method === Mfa.Sms && !user.phone) && (
          <Button size="small" type="primary" onClick={() => setEnableMfaMethod(method)}>
            Enable
          </Button>
        )}
      </Space>
    );
  };

  return (
    <Page>
      <Page.Header title="User profile" extra={<Button type="primary" onClick={() => setEditUser(true)}>Edit profile</Button>} />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Descriptions column={1}>
              <Descriptions.Item label="Email">{user?.email}</Descriptions.Item>
              <Descriptions.Item label="First name">{user?.firstname}</Descriptions.Item>
              <Descriptions.Item label="Last name">{user?.lastname}</Descriptions.Item>
              <Descriptions.Item label="Phone"><PhoneNumber number={user?.phone} /></Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={24}>
          <Card title="Multi-factor authentication">
            <List
              size="small"
              dataSource={ALL_MFA_METHODS}
              renderItem={(method) => (
                <List.Item>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    gap: 16,
                  }}
                  >
                    <Space size="middle" wrap={false}>
                      <Typography.Text strong>{MFA_METHOD_LABELS[method]}</Typography.Text>
                      <Typography.Text type="secondary">{getMethodDescription(method)}</Typography.Text>
                    </Space>
                    {getMethodActions(method)}
                  </div>
                </List.Item>
              )}
            />

          </Card>
        </Col>
        <Col span={24}>
          <Card title="Accounts">
            <Table dataSource={accountAccess} columns={accountColumns} />
          </Card>
        </Col>
        <Col span={24}>
          <Card title="Data sources">
            <Table dataSource={dataSourceAccess} columns={dataSourceColumns} />
          </Card>
        </Col>
      </Row>
      <EditUserProfile user={editUser && user} onCancel={() => setEditUser(false)} />
      <EnableMFA
        user={enableMfaMethod && user}
        mfaMethod={enableMfaMethod}
        reconfigure={enableMfaMethod === Mfa.Totp && user.totpEnabled}
        onCancel={async () => {
          setEnableMfaMethod(undefined);
          await refetch();
        }}
      />
    </Page>
  );
};

export default UserProfile;
